import { useSelector } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import type { TextProps } from "@similarweb/ui-components/foundations";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import OptionsMenuDropdown from "app/pages/sales-intelligence/common-components/options-menu/OptionsMenu/OptionsMenuDropdown";
import { TruncatedTextWithTooltip } from "app/pages/sales-intelligence/common-components/TruncatedText";
import { useIsContactBeingRevealed } from "app/pages/sales-intelligence/sub-modules/common/hooks/useContactRevealStatus";
import AiMeetingAgentDropdownItem from "app/pages/sales-intelligence/sub-modules/contacts-search/components/cells/EngageActionsCell/AiMeetingAgentDropdownItem";
import CraftEmailDropdownItem from "app/pages/sales-intelligence/sub-modules/contacts-search/components/cells/EngageActionsCell/CraftEmailDropdownItem";
import { StyledDDItemWrapper } from "app/pages/sales-intelligence/sub-modules/contacts-search/components/cells/EngageActionsCell/styles";
import WhatsappLinkButton from "app/pages/sales-intelligence/sub-modules/contacts-search/components/cells/EngageActionsCell/WhatsappLinkButton";
import ContactsExportCommonItems from "app/pages/sales-intelligence/sub-modules/contacts-search/components/ContactsExportDropdown/ContactsExportCommonItems";
import { selectIsExportFlowActive } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import type {
    ContactSearchEntry,
    CRMEntity,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";

import { ShrinkableFlex, ShrinkableStack, TruncatableTitleWrapper } from "./styles";

const LONG_COUNTRY_NAME_LENGTH = 10;

type Props = {
    contact: ContactSearchEntry;
    onExportToCRM: (contact: ContactSearchEntry, destination: Integration, as?: CRMEntity) => void;
};

export const ContactWidgetHeader = ({ contact, onExportToCRM }: Props) => {
    const { name, title, country, linkedin } = contact;
    const { translate } = useTranslate();

    const isLongCountryName = country.length > LONG_COUNTRY_NAME_LENGTH;
    const CountryNameComponent = (
        isLongCountryName ? TruncatedTextWithTooltip : Text
    ) as React.ComponentType<TextProps>;

    const countryNameMinWidth: string = isLongCountryName ? "65px" : "unset";

    const isContactBeingRevealed = useIsContactBeingRevealed(contact.id);
    const isExportOngoing = useSelector(selectIsExportFlowActive);

    const hasDataToExport = contact.hasValidEmails || contact.hasDirectPhoneNumbers || contact.hasMobilePhoneNumbers;
    const isExportDisabled = !hasDataToExport || isContactBeingRevealed || isExportOngoing;

    const disabledExportTooltip = isContactBeingRevealed
        ? translate("si.contacts_search.action.export.not-revealed-tooltip")
        : "";

    const handleCRMExport = (destination: Integration, as: CRMEntity = "contact") => {
        onExportToCRM(contact, destination, as);
    };

    // Enables hover styling (canBeHovered) in ExportVariantsDropdownItem
    const noop = () => {};

    return (
        <Flex
            justifyContent="space-between"
            margin="0 0 6px 0"
        >
            <ShrinkableStack gap="2px">
                <ShrinkableFlex
                    gap="4px"
                    alignItems="center"
                >
                    <TruncatedTextWithTooltip
                        fontVariant="body2"
                        color={colorsPalettes.carbon[400]}
                        data-automation="contact-widget-name"
                    >
                        {name}
                    </TruncatedTextWithTooltip>
                    {linkedin ? (
                        <Tooltip content={translate("si.contacts_search.contacts.linked_in.tooltip")}>
                            <Box
                                as="a"
                                display="inline-flex"
                                alignItems="center"
                                flexShrink="0"
                                href={linkedin}
                                target="_blank"
                                rel="noreferrer noopener"
                                data-automation="contact-widget-linkedin-link"
                            >
                                <SWReactIcons
                                    iconName="linkedin-circle-blue"
                                    size="xs"
                                />
                            </Box>
                        </Tooltip>
                    ) : null}
                </ShrinkableFlex>
                <ShrinkableFlex gap="8px">
                    <TruncatableTitleWrapper>
                        <TruncatedTextWithTooltip
                            fontVariant="listItem3"
                            color={colorsPalettes.carbon[250]}
                            data-automation="contact-widget-title"
                        >
                            {title}
                        </TruncatedTextWithTooltip>
                    </TruncatableTitleWrapper>
                    <Text
                        fontVariant="listItem3"
                        color={colorsPalettes.carbon[250]}
                    >
                        •
                    </Text>
                    <CountryNameComponent
                        fontVariant="listItem3"
                        color={colorsPalettes.carbon[250]}
                        minWidth={countryNameMinWidth}
                        data-automation="contact-widget-country"
                    >
                        {country}
                    </CountryNameComponent>
                </ShrinkableFlex>
            </ShrinkableStack>
            <Box
                width="20px"
                height="20px"
                padding="0 8px 0 0"
            >
                <OptionsMenuDropdown
                    id={`contact-widget-header-${contact.id}`}
                    placement="bottom-left"
                    buttonIconSize="xs"
                    tooltipContent={translate("si.common.more")}
                    dropdownWidth={240}
                >
                    <StyledDDItemWrapper>
                        <CraftEmailDropdownItem
                            domain={contact.site}
                            recipient={contact.name}
                            onClick={noop}
                        />
                        <AiMeetingAgentDropdownItem
                            domain={contact.site}
                            attendee={contact.name}
                            onClick={noop}
                        />

                        <ContactsExportCommonItems
                            isExportDisabled={isExportDisabled}
                            isUseLockedIcon={false}
                            disabledExportTooltip={disabledExportTooltip}
                            onCRMExport={handleCRMExport}
                            withExcelExport={false}
                        />
                        <WhatsappLinkButton
                            rowData={contact}
                            onClick={noop}
                        />
                    </StyledDDItemWrapper>
                </OptionsMenuDropdown>
            </Box>
        </Flex>
    );
};
